import {
  Box,
  Card,
  Pagination,
  Select,
  type SkeletonProps,
} from '@mantine/core'
import { type UseFieldReturnType } from '@mantine/form'
import { type TBreadCrumbItems } from '@v2/commons/components/Breadcrumb'
import EmptyPersons, {
  type EmptyPersonsProps,
} from '@v2/commons/components/EmptyBlock/EmptyPersons.component'
import LoadingSkeleton from '@v2/commons/components/LoadingSkeletonRows/LoadingSkeletonRows.component'
import BasicTemplate from '@v2/commons/templates/BasicTemplate'
import { PropsWithChildren } from 'react'

type TablePaginationProps = {
  breadCrumbItems: TBreadCrumbItems
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  isRecords: boolean
  header: React.ReactNode
  totalRecords?: number
  currentPage: number
  handlePaginate: (page: number) => void
  paginationLimitField: UseFieldReturnType<string>
  emptyPageTerm: EmptyPersonsProps['term']
  title: string
  loadingSkeletonProps?: SkeletonProps
  filters: React.ReactNode
} & PropsWithChildren

const TablePaginaitonTemplate = ({
  header,
  filters,
  isLoading,
  isFetching,
  isError,
  isRecords,
  breadCrumbItems,
  children,
  handlePaginate,
  paginationLimitField,
  title,
  emptyPageTerm,
  totalRecords = 0,
  currentPage = 1,
  loadingSkeletonProps = {
    height: '20px',
    radius: 'md',
  },
}: TablePaginationProps) => {
  return (
    <BasicTemplate
      isLoading={isLoading}
      title={title}
      header={header}
      breadCrumbItems={breadCrumbItems}
    >
      <Card shadow="lg" radius="md" style={{ padding: '36px' }} mih={'70vh'}>
        {filters && <Box>{filters}</Box>}
        {isFetching ? (
          <LoadingSkeleton
            maxRange={parseInt(paginationLimitField.getValue())}
            {...loadingSkeletonProps}
          />
        ) : isError || isRecords ? (
          <>
            {children}
            <Box mb={20} style={{ position: 'relative' }}>
              <Pagination
                total={Math.ceil(
                  totalRecords / parseInt(paginationLimitField.getValue()),
                )}
                boundaries={1}
                siblings={1}
                onChange={handlePaginate}
                value={currentPage}
                mt={5}
                styles={{
                  root: {
                    display: 'flex',
                    justifyContent: 'center',
                  },
                }}
              />
              <Select
                data={['5', '10', '25', '50']}
                label="Show #"
                {...paginationLimitField.getInputProps()}
                checkIconPosition="right"
                allowDeselect={false}
                styles={{
                  root: {
                    position: 'absolute',
                    right: 0,
                    top: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  },
                  input: {
                    width: '65px',
                    textAlign: 'center',
                  },
                }}
              />
            </Box>
          </>
        ) : (
          <EmptyPersons term={emptyPageTerm} />
        )}
      </Card>
    </BasicTemplate>
  )
}

export default TablePaginaitonTemplate
